import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { _noop } from 'utils/function.utils';
import { ImageUploaderStore } from './image-uploader.store';
import { Button } from '../../buttons/button';
import { ButtonType } from '../../buttons/button-types';
import styles from './image-uploader.module.scss';

export interface ImageCropValues {
  scale?: number;
  rotate?: number;
  aspect?: number;

  showPreview?: boolean;
  showActions?: boolean;
}

export const ImageUploader = observer(
  ({
    config = {},
    value,
    disabled = false,
    onChange = _noop,
    onSubmit = _noop,
    onCancel = _noop,
    debounceTime = 200,
  }: {
    config?: ImageCropValues;
    disabled?: boolean;
    value?: string;
    onChange?: (e: any) => void;
    onSubmit?: (e: any) => void;
    onCancel?: () => void;
    debounceTime?: number;
  }) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const croppedImage = useRef<any>(null);
    const canvasRef = useRef<any>(null);
    const store = useLocalObservable(() => new ImageUploaderStore(config, imgRef.current, value, disabled));
    const { t } = useTranslation();

    const handleFiles = (files: any) => {
      store.changeCrop({} as PixelCrop);
      const reader = new FileReader();
      reader.addEventListener('load', () => store.changeImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(files);
    };

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (store.aspect) {
        const { width, height } = e.currentTarget;
        store.changeCrop(
          centerCrop(
            makeAspectCrop(
              {
                unit: '%',
                width: 90,
              },
              store.aspect,
              width,
              height
            ),
            width,
            height
          ) as Crop
        );
      }
    };

    // handle drag events
    const handleDrag = function (e: any) {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'dragenter' || e.type === 'dragover') {
        store.changeDragActive(true);
      } else if (e.type === 'dragleave') {
        store.changeDragActive(false);
      }
    };

    // triggers when file is dropped
    const handleDrop = function (e: any) {
      e.preventDefault();
      e.stopPropagation();
      store.changeDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFiles(e.dataTransfer.files[0]);
      }
    };

    // triggers when file is selected with click
    const handleChange = function (e: any) {
      e.preventDefault();
      if (e.target.files && e.target.files[0]) {
        handleFiles(e.target.files[0]);
      }
    };

    useEffect(() => {
      if (imgRef.current) {
        store.changeImageRef(imgRef.current);
      }
    }, [imgRef, store]);

    const submit = (value: string | undefined) => {
      onSubmit(value);
    };

    const makeClientCrop = async (crop: any) => {
      if (imgRef.current && crop.width && crop.height) {
        croppedImage.current = await getCroppedImg(imgRef.current, crop, 'newFile.jpeg');
      }
    };

    const getCroppedImg = (image: any, crop: any, fileName: any) => {
      if (!canvasRef.current || !imgRef.current) {
        return;
      }
      const canvas = canvasRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext('2d');

      canvas.width = crop.width * 1;
      canvas.height = crop.height * 1;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );

      return new Promise((resolve, reject) => {
        resolve(canvas.toDataURL());
        // Note: the below will save to image as a blob
        //   canvas.toBlob((blob: any) => {
        //     if (!blob) {
        //       //reject(new Error('Canvas is empty'));
        //       console.error('Canvas is empty');
        //       return;
        //     }
        //     blob.name = fileName;
        //     resolve(blob);
        //   }, 'image/jpeg');
      });
    };

    const handleComplete = (c: any) => {
      makeClientCrop(c).then((ress) => {
        store.changeCompletedCrop(croppedImage.current);
      });
    };

    return (
      <div className={styles.imageUploaderContainer}>
        <div className={styles.inputArea}>
          <input type="file" accept="image/png, image/jpeg, image/jpg" onInput={handleChange} ref={inputRef} />
          {!store.imgSrc && (
            <div
              className={`${styles.input} ${store.dragActive ? styles.dragActive : ''}`}
              onClick={() => {
                inputRef.current?.click();
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <span>Drag and drop or click here</span>
            </div>
          )}
        </div>

        {store.imgSrc && (
          <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
            <ReactCrop
              crop={store.crop?.height ? store.crop : undefined}
              onChange={(_, percentCrop) => store.changeCrop(percentCrop as any)}
              onComplete={handleComplete}
              aspect={store.aspect}
              circularCrop={true}
            >
              <img
                ref={imgRef}
                alt="Cropping Selection"
                src={store.imgSrc}
                className={`${store.dragActive ? styles.dragActive : ''}`}
                style={{
                  transform: `scale(${store.scale}) rotate(${store.rotate}deg)`,
                }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </div>
        )}
        <div>
          <canvas className={styles.canvasElement} ref={canvasRef} />
        </div>

        <div>
          {store.previewSrc && false && (
            <div className={styles.previewContainer}>
              <img alt="Crop preview" src={store.previewSrc} />
            </div>
          )}
        </div>

        {config.showActions !== false && (
          <div className={`row ${styles.popupActionButtons} ${styles.continueButton}`}>
            <Button
              classes={[styles.popupButton, 'cust-mr-1']}
              meta={{
                title: t('buttons.cancel'),
                name: 'Cancel',
                type: ButtonType.OutlineDark,
              }}
              onClick={() => {
                store.reset();
                onCancel();
              }}
            ></Button>
            <Button
              classes={[styles.popupButton, 'cust-ml-1']}
              meta={{
                title: t('buttons.save'),
                name: 'Save',
                type: ButtonType.Primary,
              }}
              onClick={() => submit(store.croppedImage)}
            ></Button>
          </div>
        )}
      </div>
    );
  }
);